/**
 * Pure geometry for `FlowConnector` curves. Produces N cubic-Bezier path
 * strings that fan out at the icon edge and converge into a tight cluster
 * at the hub edge — the "merge before the node" shape.
 *
 * Both sides use the same 0-100 `viewBox`. Paths always start at x=0 and
 * end at x=100; the molecule scales the SVG via CSS so the path strings
 * are layout-independent.
 *
 *   side="left"   icon is at x=0, hub is at x=100  (paths flow → hub)
 *   side="right"  hub is at x=0, icon is at x=100  (paths flow → icon)
 *
 * Tunable constants are intentionally not exposed as props — the molecule
 * owns the geometric language. Add a prop here only when a real callsite
 * needs a different aesthetic.
 */

const CENTER_Y = 50;
// All paths converge to a single point on the cluster edge.
const HUB_CLUSTER_SPREAD = 0;
// Both control points sit on the cluster Y line, so each path reads as a
// long single line that breaks into its target iconY only near the very
// end. Pushes the visible "divergence point" deep into the icon side.
const CP_HOLD_OFFSET = 50;
const CP_TIP_OFFSET = 14;
// Length of the straight tail at the icon edge — gives the line a clear
// direction "into" its tile rather than ending mid-curve.
const ICON_TAIL_LENGTH = 8;
const MAX_ICON_SPREAD = 88;

/**
 * Total vertical extent (in viewBox units) of the icon-side endpoints
 * for a given path count. Values match the row centers of an IconCloud
 * laid out as N tiles of 40px stacked with 12px gaps inside a connector
 * of the same total height. The middle path always lands at y=50; the
 * outer paths land on the outermost tile rows.
 */
const ICON_SPREAD_BY_COUNT: Readonly<Record<number, number>> = {
  1: 0,
  2: 56,
  3: 72,
  4: 80,
};

export function computeFlowPaths({
  count,
  side,
}: {
  count: number;
  side: "left" | "right";
}): readonly string[] {
  if (count <= 0) {
    return [];
  }

  const iconYs = distributeY(count, iconTotalSpread(count));
  const hubYs = distributeY(count, HUB_CLUSTER_SPREAD);

  return iconYs.map((iconY, index) => {
    const hubY = hubYs[index] ?? CENTER_Y;
    if (side === "left") {
      // Start at icon edge, end at hub cluster. A short horizontal tail
      // at the icon side points the line straight at its tile; the bend
      // then sweeps up to the cluster.
      return `M 0 ${f(iconY)} L ${ICON_TAIL_LENGTH} ${f(iconY)} C ${ICON_TAIL_LENGTH + CP_TIP_OFFSET} ${f(iconY)} ${100 - CP_HOLD_OFFSET} ${f(hubY)} 100 ${f(hubY)}`;
    }
    // Right side: start at hub cluster, end at icon edge. Bend lands at
    // iconY a few units shy of the edge, then a short horizontal tail
    // points the line straight at its tile.
    return `M 0 ${f(hubY)} C ${CP_HOLD_OFFSET} ${f(hubY)} ${100 - ICON_TAIL_LENGTH - CP_TIP_OFFSET} ${f(iconY)} ${100 - ICON_TAIL_LENGTH} ${f(iconY)} L 100 ${f(iconY)}`;
  });
}

function iconTotalSpread(count: number): number {
  return ICON_SPREAD_BY_COUNT[count] ?? MAX_ICON_SPREAD;
}

function distributeY(count: number, spread: number): number[] {
  if (count <= 0) {
    return [];
  }
  if (count === 1) {
    return [CENTER_Y];
  }
  const step = spread / (count - 1);
  return Array.from(
    { length: count },
    (_, i) => CENTER_Y + (i - (count - 1) / 2) * step
  );
}

function f(value: number): string {
  return Number(value.toFixed(2)).toString();
}
